import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Btn, Modal, Empty } from "./UI";
import { SlidePanel } from "./SlidePanel";
import { FSelect } from "../pages/engagement/FormControls";
import { History, Archive, Ban } from "lucide-react";
import { toast } from "sonner";

export const DX_TYPES = ["Principal", "Secondary", "Provisional", "Rule-out", "By History"];
export const DX_TYPE_LABELS = { Principal: "Primary (Principal)", Secondary: "Secondary", Provisional: "Provisional", "Rule-out": "Rule-out", "By History": "By History" };
export const DX_TYPE_HINT = {
  "By History": "Diagnosed elsewhere (for example on records the client brought in). Not yet confirmed by this clinic.",
  Provisional: "First impression. Needs more visits before it becomes the principal diagnosis.",
  "Rule-out": "Considered and ruled out. The ruled-out date is recorded automatically.",
};

/** Only the clinician who added a diagnosis, or an admin, can remove, void, or rename it. */
export const canRemoveDx = (user, d) => !!user && (user.role === "admin" || [d.created_by_id, d.diagnosed_by_id].filter(Boolean).includes(user.id));

/** Soft notice when another current diagnosis is already marked Principal. It never blocks saving. */
export function PrincipalNote({ items, form, editId, testId }) {
  if (form.dx_type !== "Principal") return null;
  const other = items.find((x) => x.id !== editId && x.dx_type === "Principal" && !["Archived", "Voided", "Resolved"].includes(x.status));
  if (!other) return null;
  return <p className="mt-1 text-[11px] font-semibold text-[#8D5A00]" data-testid={testId}>This client already has a Principal diagnosis: {other.icd10_code || other.code} {other.description}. Consider Secondary for this one.</p>;
}

export function useClinicians() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => setRows((r.data || []).filter((u) => ["clinician", "admin"].includes(u.role) && u.status !== "inactive"))).catch(() => {});
  }, []);
  return rows;
}

const fullName = (u) => {
  const creds = u.credentials || u.prescriber_credentials;
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() + (creds ? `, ${creds}` : "");
};

/** Diagnosing clinician select. Stores both the display name (with credentials) and the user id. */
export function ClinicianSelect({ value, valueId, onChange, testId }) {
  const users = useClinicians();
  const known = users.find((u) => u.id === valueId);
  return (
    <FSelect data-testid={testId} value={known ? valueId : value ? "__legacy" : ""} placeholder="Select clinician"
      onChange={(e) => { const u = users.find((x) => x.id === e.target.value); if (u) onChange({ name: fullName(u), id: u.id }); }}>
      {value && !known && <option value="__legacy">{value}</option>}
      {users.map((u) => <option key={u.id} value={u.id}>{fullName(u)}</option>)}
    </FSelect>
  );
}

function ReasonModal({ dx, onClose, onDone, title, intro, url, min, testPrefix, cta, Icon, success }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [dx]);
  const submit = async () => {
    setBusy(true);
    try { await api.post(url, { reason: reason.trim() }); toast.success(success); onDone(); }
    catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Modal open={!!dx} onClose={onClose} title={title}>
      <p className="text-sm text-[#5D4037]">{intro}</p>
      <label className="mt-4 mb-1 block text-xs font-bold uppercase text-[#7B6157]" htmlFor={`${testPrefix}-reason`}>Reason (required, at least {min} characters)</label>
      <textarea id={`${testPrefix}-reason`} data-testid={`${testPrefix}-reason`} rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
        placeholder="For example: entered on the wrong client, or superseded by a confirmed diagnosis"
        className="w-full rounded-lg border border-[#E5DFD3] px-3 py-2 text-sm outline-none focus:border-[#8D6E63]" />
      <div className="mt-4 flex justify-end gap-2">
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn data-testid={`${testPrefix}-confirm`} disabled={busy || reason.trim().length < min} onClick={submit}><Icon size={14} /> {busy ? "Saving" : cta}</Btn>
      </div>
    </Modal>
  );
}

export function ArchiveDiagnosisDialog({ dx, onClose, onDone }) {
  return <ReasonModal dx={dx} onClose={onClose} onDone={onDone} title="Archive diagnosis" min={3} testPrefix="dx-archive" cta="Archive" Icon={Archive}
    url={`/diagnoses/${dx?.id}/archive`} success="Diagnosis archived. It stays in the history."
    intro={`Archive ${dx?.code || dx?.icd10_code || ""} ${dx?.description || ""}. Archived diagnoses leave the active chart but remain in the history and audit log.`} />;
}

export function VoidDiagnosisDialog({ dx, onClose, onDone }) {
  return <ReasonModal dx={dx} onClose={onClose} onDone={onDone} title="Void diagnosis entered in error" min={5} testPrefix="dx-void" cta="Void diagnosis" Icon={Ban}
    url={`/diagnoses/${dx?.id}/void`} success="Diagnosis voided. Only admins can view it now."
    intro={`Void ${dx?.code || dx?.icd10_code || ""} ${dx?.description || ""} if it was entered by mistake. It disappears from every list, cannot be restored, and only admins can view it with the reason.`} />;
}

const ACTION_TONE = { added: "bg-[#E8F5E9] text-[#2E7D32]", updated: "bg-[#E3F2FD] text-[#1565C0]", archived: "bg-[#ECEFF1] text-[#546E7A]", restored: "bg-[#FFF3E0] text-[#8D5A00]", voided: "bg-[#FFEBEE] text-[#C62828]" };

export function DiagnosisHistoryPanel({ client, open, onClose, diagnosis }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) api.get(`/clients/${client.id}/diagnoses/history`).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  }, [open, client.id]);
  const shown = diagnosis ? rows.filter((h) => h.diagnosis_id === diagnosis.id) : rows;
  return (
    <SlidePanel open={open} onClose={onClose} title={diagnosis ? `History: ${diagnosis.description}` : "Diagnosis History"}
      subtitle={`${client.first_name} ${client.last_name}: every add, type change, edit, archive, void, and restore`} testId="dx-history-panel">
      {shown.length === 0 ? <Empty text="No diagnosis changes recorded yet" /> : (
        <ol className="relative space-y-4 border-l-2 border-[#EFEBE0] pl-5">
          {shown.map((h) => (
            <li key={h.id} data-testid={`dx-history-row-${h.id}`} className="relative">
              <span className="absolute -left-[27px] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-white ring-2 ring-[#D7CCC8]"><History size={9} className="text-[#7B6157]" /></span>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${ACTION_TONE[h.action] || ACTION_TONE.updated}`}>{h.action}</span>
                <span className="font-mono text-xs font-bold text-[#3E2723]">{h.snapshot?.code}</span>
                <span className="text-sm text-[#4E342E]">{h.snapshot?.description}</span>
                {h.snapshot?.dx_type && <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-semibold text-[#6D4C41]">{DX_TYPE_LABELS[h.snapshot.dx_type] || h.snapshot.dx_type}</span>}
              </div>
              <div className="mt-0.5 text-[11px] text-[#7B6157]">{dayjs(h.at).format("MMM D, YYYY h:mm A")} by {h.by_name}</div>
              {h.reason && <div className="mt-1 text-xs text-[#5D4037]">Reason: {h.reason}</div>}
              {h.changes?.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-xs text-[#5D4037]">
                  {h.changes.map((c) => <li key={c.field}><span className="font-semibold">{c.field.replace(/_/g, " ")}</span>: {String(c.from || "blank")} to {String(c.to || "blank")}</li>)}
                </ul>
              )}
            </li>
          ))}
        </ol>
      )}
    </SlidePanel>
  );
}

export function DiagnosisListToggles({ showArchived, setShowArchived, showVoided, setShowVoided, isAdmin, onHistory, testPrefix }) {
  return (
    <>
      <label className="flex items-center gap-1.5 text-xs text-[#6D4C41]">
        <input type="checkbox" data-testid={`${testPrefix}-show-archived`} checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-3.5 w-3.5" />
        Show archived
      </label>
      {isAdmin && setShowVoided && <label className="flex items-center gap-1.5 text-xs text-[#C62828]">
        <input type="checkbox" data-testid={`${testPrefix}-show-voided`} checked={showVoided} onChange={(e) => setShowVoided(e.target.checked)} className="h-3.5 w-3.5" />
        Show voided (admin)
      </label>}
      <Btn variant="ghost" data-testid={`${testPrefix}-history-btn`} onClick={onHistory}><History size={14} /> History</Btn>
    </>
  );
}
